import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import Swal, {
  SweetAlertOptions,
  SweetAlertPosition,
  SweetAlertResult,
} from 'sweetalert2';

@Injectable({
  providedIn: 'root',
})
export class SwalService {
  private translate = inject(TranslateService);

  private getPosition(): SweetAlertPosition {
    const lang = this.translate.currentLang || 'ar';
    return lang === 'en' ? 'top-end' : 'top-start';
  }

  /**
   * Ouvre une alerte en la rendant visible par-dessus n'importe quoi.
   *
   * <p>Deux obstacles distincts, et un seul remède.</p>
   *
   * <p><b>La couche supérieure.</b> Un dialogue ouvert par
   * <code>showModal()</code> y vit ; tout ce qui reste dans le document est
   * peint dessous, quel que soit son <code>z-index</code>. Aucune valeur, si
   * haute soit-elle, ne passe devant.</p>
   *
   * <p><b>Les contextes d'empilement.</b> Une modale ordinaire n'a pas ce
   * privilège, mais un ancêtre portant <code>transform</code>,
   * <code>filter</code> ou <code>backdrop-filter</code> enferme l'alerte dans
   * un contexte dont elle ne peut plus sortir — elle passe alors derrière une
   * simple <code>&lt;div&gt;</code>.</p>
   *
   * <p>Le remède couvre les deux : on promeut le conteneur de l'alerte dans la
   * couche supérieure, via l'API Popover. Il y entre <i>après</i> le dialogue,
   * donc au-dessus — la couche s'empile par ordre d'entrée, pas par
   * <code>z-index</code> — et il échappe à tout contexte d'empilement.</p>
   *
   * <p>Sans cette API (navigateur ancien), on retombe sur l'ancien détour :
   * rendre l'alerte <i>à l'intérieur</i> du dialogue ouvert, qui est déjà dans
   * la couche. Moins bon — un corps de dialogue qui défile peut la rogner —
   * mais toujours visible.</p>
   *
   * <p>Fait ici plutôt que chez chaque appelant : le piège est invisible à
   * l'écriture et se paierait sur chaque nouvel écran.</p>
   */
  private fire(options: SweetAlertOptions): Promise<SweetAlertResult> {
    const canPromote = SwalService.supportsPopover();

    return Swal.fire({
      target: canPromote ? document.body : this.topLayerHost(),
      ...options,
      didOpen: (popup) => {
        if (canPromote) SwalService.promoteToTopLayer(popup);
        options.didOpen?.(popup);
      },
      didClose: () => {
        if (canPromote) SwalService.demoteFromTopLayer();
        options.didClose?.();
      },
    });
  }

  /** L'API Popover est-elle disponible ? Elle seule donne accès à la couche supérieure. */
  private static supportsPopover(): boolean {
    return typeof HTMLElement !== 'undefined'
      && typeof HTMLElement.prototype.showPopover === 'function';
  }

  /**
   * Fait entrer le conteneur de l'alerte dans la couche supérieure.
   *
   * <p><code>popover="manual"</code> : c'est nous qui ouvrons et fermons.
   * Le mode automatique se refermerait au premier clic hors du panneau, y
   * compris sur le bouton « Confirmer ».</p>
   */
  private static promoteToTopLayer(popup: HTMLElement): void {
    const container = popup.closest<HTMLElement>('.swal2-container');
    if (!container) return;

    try {
      container.setAttribute('popover', 'manual');
      container.showPopover();
    } catch {
      // Un conteneur déjà promu relève : sans conséquence, l'alerte est
      // visible de toute façon. On n'en fait pas échouer l'ouverture.
    }
  }

  /** Referme le popover à la fermeture de l'alerte, pour ne rien laisser dans la couche. */
  private static demoteFromTopLayer(): void {
    const container = document.querySelector<HTMLElement>('.swal2-container');
    if (!container?.hasAttribute('popover')) return;

    try {
      container.hidePopover();
    } catch {
      /* Déjà refermé — SweetAlert retire le conteneur du document juste après. */
    }
    container.removeAttribute('popover');
  }

  /** Le dialogue modal ouvert le plus récent, s'il y en a un. */
  private topLayerHost(): HTMLElement {
    const open = Array.from(
      document.querySelectorAll<HTMLDialogElement>('dialog[open]'),
    );

    // `:modal` ne vise que les dialogues ouverts par showModal() — ceux ouverts
    // par show() restent dans le flux et n'ont pas ce problème. Le dernier du
    // document est le plus haut dans la couche supérieure.
    //
    // `matches()` **lève** sur un sélecteur inconnu : un navigateur sans
    // `:modal` ferait échouer toutes les alertes de l'application. On retombe
    // alors sur le dernier dialogue ouvert, qui est le bon dans la quasi-
    // totalité des cas.
    for (let i = open.length - 1; i >= 0; i--) {
      const dialog = open[i];
      try {
        if (dialog.matches(':modal')) return dialog;
      } catch {
        return dialog;
      }
    }

    return document.body;
  }

  success(message: string) {
    const title = this.translate.instant('SWAL.SUCCESS');
    this.fire({
      toast: true,
      position: this.getPosition(),
      icon: 'success',
      title,
      text: message,
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });
  }

  error(message: string) {
    const title = this.translate.instant('SWAL.ERROR');
    this.fire({
      icon: 'error',
      title,
      text: message,
    });
  }

  confirm(message: string, confirmCallback: () => void) {
    const title = this.translate.instant('SWAL.CONFIRM');
    this.fire({
      title,
      text: message,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: this.translate.instant('SWAL.YES'),
      cancelButtonText: this.translate.instant('SWAL.NO'),
    }).then((result) => {
      if ((result as SweetAlertResult).isConfirmed) confirmCallback();
    });
  }

  async confirmAsync(message: string): Promise<boolean> {
    const title = this.translate.instant('SWAL.CONFIRM');

    const result = await this.fire({
      title,
      text: message,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: this.translate.instant('SWAL.YES'),
      cancelButtonText: this.translate.instant('SWAL.NO'),
    });

    return result.isConfirmed;
  }

  warning(message: string) {
    const title = this.translate.instant('SWAL.WARNING');
    this.fire({
      icon: 'warning',
      title,
      text: message,
      confirmButtonText: this.translate.instant('SWAL.OK'),
    });
  }

  /**
   * Avertissement dont le corps est du HTML — pour énumérer plusieurs cas.
   *
   * <p><code>warning</code> ne prend que du texte, et SweetAlert y ignore les
   * retours à la ligne : une liste de trois anomalies s'y écrasait en un seul
   * paragraphe illisible. L'appelant reste responsable de ce qu'il injecte.</p>
   */
  warningHtml(html: string, title?: string) {
    this.fire({
      icon: 'warning',
      title: title || this.translate.instant('SWAL.WARNING'),
      html,
      confirmButtonText: this.translate.instant('SWAL.OK'),
    });
  }

  confirmPublishToggle(isPublished: boolean, confirmCallback: () => void) {
    const title = this.translate.instant(
      isPublished ? 'EXAM.UNPUBLISH_TITLE' : 'EXAM.PUBLISH_TITLE'
    );
    const text = this.translate.instant(
      isPublished ? 'EXAM.UNPUBLISH_MSG' : 'EXAM.PUBLISH_MSG'
    );
    const confirmButtonText = this.translate.instant(
      isPublished ? 'EXAM.UNPUBLISH' : 'EXAM.PUBLISH'
    );
    const cancelButtonText = this.translate.instant('COMMON.CANCEL');

    this.fire({
      title,
      text,
      icon: isPublished ? 'warning' : 'info',
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText,
      confirmButtonColor: isPublished ? '#d33' : '#28a745',
    }).then((result) => {
      if (result.isConfirmed) confirmCallback();
    });
  }

  info(message: string, title?: string) {
    const dialogTitle = title || this.translate.instant('SWAL.INFO');
    this.fire({
      icon: 'info',
      title: dialogTitle,
      html: message,
      confirmButtonText: this.translate.instant('SWAL.OK'),
      confirmButtonColor: '#3085d6',
    });
  }
}
